import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, TrendingUp, Sparkles, Store, Rocket, Trophy, Building2 } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const ICON_MAP = {
  store: Store,
  building: Building2,
  trophy: Trophy,
  trending: TrendingUp,
  rocket: Rocket,
  sparkles: Sparkles,
  map: MapPin,
};

function renderTitle(text) {
  if (!text) return null;
  const parts = text.split(/(<em>[^<]*<\/em>)/g);
  return parts.map((p, i) => {
    const m = p.match(/^<em>([^<]*)<\/em>$/);
    if (m) return <span key={i} className="accent">{m[1]}</span>;
    return <span key={i}>{p}</span>;
  });
}

function toFullUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

export default function TimelineHistoria({ block }) {
  const items = useMemo(() => block?.items || [], [block]);
  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef(null);
  const railRef = useRef(null);

  // Auto-advance (pauses on hover)
  useEffect(() => {
    if (!items.length || hovering) return;
    timerRef.current = setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [items.length, hovering]);

  // Scroll the year into view on mobile (horizontal only — never moves the page)
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const el = rail.querySelector(`[data-year-idx="${active}"]`);
    if (!el) return;
    const left = el.offsetLeft - (rail.clientWidth - el.clientWidth) / 2;
    rail.scrollTo({ left, behavior: "smooth" });
  }, [active]);

  const go = (dir) => setActive((v) => (v + dir + items.length) % items.length);
  const current = items[active] || {};
  const CurrentIcon = ICON_MAP[current.icon] || TrendingUp;

  if (!items.length) return null;

  return (
    <section id="historia" className="section-elev py-24 md:py-32 relative overflow-hidden">
      <div className="gold-glow" style={{ width: 520, height: 520, top: -140, left: -180, opacity: 0.5 }} />
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div className="max-w-2xl">
            <Reveal><span className="eyebrow">{block?.eyebrow || "Historia · 2018 → hoy → futuro"}</span></Reveal>
            <Reveal delay={120}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]">
                {renderTitle(block?.title || "8 años construyendo lo que hoy podemos <em>escalar</em>.")}
              </h2>
            </Reveal>
            {block?.subtitle && (
              <Reveal delay={220}>
                <p className="mt-5 text-[#3F3F46] text-base md:text-lg leading-relaxed max-w-xl">{block.subtitle}</p>
              </Reveal>
            )}
          </div>
          <Reveal delay={280}>
            <div className="flex items-center gap-2">
              <button onClick={() => go(-1)} className="w-11 h-11 rounded-full bg-black/[0.03] border border-black/10 hover:border-[#FFBF57] hover:bg-[#FFBF57]/10 transition-all flex items-center justify-center text-[#3F3F46] hover:text-[#0A0A0A]" aria-label="Anterior" data-testid="timeline-prev">
                <ChevronLeft size={18} />
              </button>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B] px-2 tabular-nums">
                {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </div>
              <button onClick={() => go(1)} className="w-11 h-11 rounded-full bg-black/[0.03] border border-black/10 hover:border-[#FFBF57] hover:bg-[#FFBF57]/10 transition-all flex items-center justify-center text-[#3F3F46] hover:text-[#0A0A0A]" aria-label="Siguiente" data-testid="timeline-next">
                <ChevronRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>

        {/* Rail with years */}
        <Reveal delay={200}>
          <div
            ref={railRef}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            className="relative mb-10 overflow-x-auto scrollbar-hide"
            data-testid="timeline-rail"
          >
            {/* Gold thread */}
            <div className="absolute left-0 right-0 top-[38px] h-px bg-gradient-to-r from-transparent via-[#FFBF57]/45 to-transparent" />
            {/* Active progress overlay */}
            <div
              className="absolute left-0 top-[38px] h-px bg-gradient-to-r from-[#FFBF57] to-[#FFCF7F] shadow-[0_0_12px_rgba(255,191,87,0.6)] transition-all duration-700"
              style={{ width: `${((active + 0.5) / items.length) * 100}%` }}
            />
            <div className="flex items-start justify-between gap-4 min-w-[720px] md:min-w-0 relative pb-2">
              {items.map((it, i) => {
                const isActive = i === active;
                const isPast = i < active;
                const Ic = ICON_MAP[it.icon] || TrendingUp;
                return (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-year-idx={i}
                    data-testid={`timeline-year-${i}`}
                    className="flex-1 flex flex-col items-center gap-2 group cursor-pointer relative"
                    style={{ minWidth: 90 }}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center relative z-10 transition-all ${
                        isActive
                          ? "bg-[#FFBF57] border-[#FFBF57] scale-125 shadow-[0_0_20px_rgba(255,191,87,0.7)]"
                          : isPast
                            ? "bg-[#FFBF57] border-[#FFBF57]"
                            : "bg-[#FAFAFA] border-black/12 group-hover:border-[#FFBF57]"
                      }`}
                    >
                      {isActive && <span className="absolute inset-0 rounded-full border border-[#FFBF57] ping-slow" />}
                    </div>
                    <div className={`font-mono text-xs tabular-nums transition-colors ${isActive ? "text-[#0A0A0A]" : isPast ? "text-[#FFBF57]" : "text-[#52525B] group-hover:text-[#3F3F46]"}`}>
                      {it.year}
                    </div>
                    <div className={`text-[9px] uppercase tracking-widest font-mono text-center transition-colors max-w-[110px] ${isActive ? "text-[#0A0A0A]" : "text-[#52525B] group-hover:text-[#3F3F46]"}`}>
                      {it.label}
                    </div>
                    {it.isCurrent && (
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 pill pill-gold !text-[8px] !py-0.5 !px-1.5 whitespace-nowrap">HOY</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* Detail card */}
        <Reveal delay={280} scale>
          <div
            key={active}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            className="glass-elev overflow-hidden grid md:grid-cols-[1.1fr_0.9fr] gap-0"
            data-testid="timeline-detail"
          >
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${current.isFuture ? "bg-[#FFBF57]/10 border-[#FFBF57]/40" : "bg-black/[0.03] border-black/12"}`}>
                  <CurrentIcon size={18} className={current.isFuture ? "text-[#0A0A0A]" : "text-[#0A0A0A]"} strokeWidth={1.6} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFBF57]">{current.label}</div>
                  <div className="font-mono text-3xl md:text-4xl num-mono text-gold-gradient">{current.year}</div>
                </div>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#0A0A0A] leading-tight">
                {renderTitle(current.title || "")}
              </h3>
              {current.body && (
                <p className="mt-4 text-[#3F3F46] text-base leading-relaxed">{current.body}</p>
              )}
              {current.metrics?.length > 0 && (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {current.metrics.map((m, k) => (
                    <div key={k} className="glass px-4 py-3">
                      <div className="text-xl num-mono text-gold-gradient">{m.n}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono mt-0.5">{m.l}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative min-h-[280px] md:min-h-[420px] bg-black overflow-hidden">
              {toFullUrl(current.image_url) ? (
                <img loading="lazy" decoding="async"
                  src={toFullUrl(current.image_url)}
                  alt={current.title}
                  className={`w-full h-full object-cover animate-[fadeUp_.7s_ease] ${current.isFuture ? "" : current.isCurrent ? "" : "sepia"}`}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1B1B21] via-[#1F1F26] to-[#0A0A0B] flex items-center justify-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono">Sin imagen · sube desde CMS</div>
                </div>
              )}
              <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
              <div className="absolute bottom-4 right-4 pill pill-gold !text-[9px]">
                <Sparkles size={10} />
                Capítulo {active + 1}
              </div>
            </div>
          </div>
        </Reveal>

        {block?.microcopy && (
          <div className="mt-8 text-center text-[11px] font-mono uppercase tracking-widest text-[#52525B]">{block.microcopy}</div>
        )}
      </div>
    </section>
  );
}
